import { useState, useEffect, useRef } from 'react'
import {
  Flex,
  Input,
  Text,
  HStack,
  Icon,
  IconButton,
  Tooltip,
  useColorMode,
  Spacer,
  Button,
} from '@chakra-ui/react'
import { FileText, Circle, Columns2, Monitor, Pencil, Home, FileEdit, Lock, Globe, X, Flame, Clock, Code, Share2, Check, Sun, Moon, ExternalLink, FilePlus2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { ShareFormat } from '@core/types/share'
import { validateFilename, ensureExtension, hasValidExtension } from '@/lib/utils/filename'
import { toast } from 'sonner'
import { isNeverExpires } from '@core/constants/expiry'
import { formatExpiry } from '@core/utils/format'
import { useCopyToClipboard } from '@/lib/hooks/useCopyToClipboard'

export type ViewMode = 'split' | 'editor' | 'preview'

interface EditorHeaderProps {
  filename: string
  onFilenameChange?: (filename: string) => void
  isModified?: boolean
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  hasContent?: boolean
  onEdit?: () => void
  readOnly?: boolean
  fileType?: 'draft' | 'protected' | 'public'
  isModifiable?: boolean
  onEditModifiable?: () => void
  onUpdateShare?: () => void
  isUpdatingShare?: boolean
  onClose?: () => void
  format?: ShareFormat
  onFormatChange?: (format: ShareFormat) => void
  burnAfterRead?: boolean
  expiresAt?: number
  hideViewToggle?: boolean
  shareUrl?: string
  showRawView?: boolean
  onToggleRawView?: () => void
}

export function EditorHeader({
  filename,
  onFilenameChange,
  isModified = false,
  viewMode,
  onViewModeChange,
  hasContent = false,
  onEdit,
  readOnly = false,
  fileType = 'draft',
  isModifiable = false,
  onEditModifiable,
  onUpdateShare,
  isUpdatingShare = false,
  onClose,
  format = 'md',
  onFormatChange,
  burnAfterRead,
  expiresAt,
  hideViewToggle = false,
  shareUrl,
  showRawView = false,
  onToggleRawView,
}: EditorHeaderProps) {
  const { colorMode, toggleColorMode } = useColorMode()
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)
  const [localFilename, setLocalFilename] = useState(filename)
  const [inputWidth, setInputWidth] = useState(100)
  const { copy, hasCopied } = useCopyToClipboard()
  const measureRef = useRef<HTMLParagraphElement>(null)

  const canEditFilename = !readOnly && onFilenameChange

  useEffect(() => {
    setLocalFilename(filename)
  }, [filename])

  // Measure text width and update input width
  useEffect(() => {
    if (measureRef.current && isEditing) {
      const width = measureRef.current.offsetWidth
      // Cap at 370px (400px max container - padding)
      setInputWidth(Math.min(Math.max(width + 10, 100), 370))
    }
  }, [localFilename, isEditing])

  const handleBlur = () => {
    const trimmed = localFilename.trim() || 'untitled'
    const validation = validateFilename(trimmed)

    if (!validation.valid) {
      toast.error(validation.error)
      // Keep editing mode open so user can fix the filename
      return
    }

    // Ensure filename always has an extension
    const finalFilename = hasValidExtension(trimmed)
      ? trimmed
      : ensureExtension(trimmed, format)

    setIsEditing(false)
    setLocalFilename(finalFilename)

    if (finalFilename !== filename && onFilenameChange) {
      onFilenameChange(finalFilename)
      // Update format if extension changed
      if (onFormatChange && validation.format !== format) {
        onFormatChange(validation.format)
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur()
    } else if (e.key === 'Escape') {
      setLocalFilename(filename)
      setIsEditing(false)
    }
  }

  const getButtonProps = (mode: ViewMode) => ({
    variant: viewMode === mode ? 'solid' : 'ghost' as const,
    colorScheme: viewMode === mode ? 'brand' : 'gray' as const,
    size: 'xs' as const,
    onClick: () => onViewModeChange(mode),
  })

  // Get icon and color based on file type
  const getFileIcon = () => {
    switch (fileType) {
      case 'protected':
        return { icon: Lock, color: colorMode === 'dark' ? 'stone.400' : 'stone.500' }
      case 'public':
        return { icon: Globe, color: colorMode === 'dark' ? 'brand.400' : 'brand.600' }
      case 'draft':
      default:
        return { icon: FileEdit, color: colorMode === 'dark' ? 'stone.400' : 'stone.500' }
    }
  }
  const { icon: FileIcon, color: fileIconColor } = getFileIcon()


  // Handle copy share link
  const handleCopyLink = () => {
    if (!shareUrl) return
    copy(shareUrl, 'shareLink', 'Link copied to clipboard')
  }

  return (
    <Flex
      align="center"
      px={3}
      h="45px"
      minH="45px"
      maxH="45px"
      borderBottomWidth="1px"
      borderColor="editor.border"
      bg="editor.sidebar"
      flexShrink={0}
      position="relative"
    >
      {/* Filename section */}
      <HStack spacing={2}>
        {/* Editor button - only in read-only mode */}
        {readOnly && (
          <Tooltip label="Go to editor" hasArrow placement="bottom">
            <IconButton
              aria-label="Go to editor"
              icon={<Icon as={Home} boxSize={4} />}
              variant="ghost"
              size="sm"
              onClick={() => navigate('/editor')}
            />
          </Tooltip>
        )}
        <Icon
          as={FileIcon}
          boxSize={4}
          color={fileIconColor}
        />
        {isEditing && canEditFilename ? (
          <HStack spacing={1} position="relative">
            {/* Hidden span for measuring text width */}
            <Text
              ref={measureRef}
              position="absolute"
              visibility="hidden"
              whiteSpace="pre"
              fontWeight="medium"
              fontSize="sm"
            >
              {localFilename || 'untitled'}
            </Text>
            <Input
              value={localFilename}
              onChange={(e) => setLocalFilename(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              size="xs"
              fontWeight="medium"
              fontSize="sm"
              autoFocus
              w={`${inputWidth}px`}
              minW="100px"
              bg="bg.surface"
              borderColor={colorMode === 'dark' ? 'stone.600' : 'stone.300'}
              _focus={{
                borderColor: 'brand.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
              }}
            />
          </HStack>
        ) : (
          <HStack
            spacing={1}
            cursor={canEditFilename ? 'pointer' : 'default'}
            onClick={() => canEditFilename && setIsEditing(true)}
            _hover={canEditFilename ? {
              bg: colorMode === 'dark' ? 'stone.700' : 'stone.200',
            } : undefined}
            px={2}
            py={1}
            borderRadius="md"
            transition="background 0.15s"
            maxW={readOnly ? "600px" : "400px"}
          >
            <Text
              fontSize="sm"
              fontWeight="medium"
              noOfLines={1}
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {filename || 'untitled'}
            </Text>
            {isModified && !readOnly && (
              <Icon
                as={Circle}
                boxSize={2}
                fill={colorMode === 'dark' ? 'brand.400' : 'brand.600'}
                color={colorMode === 'dark' ? 'brand.400' : 'brand.600'}
                ml={1}
                flexShrink={0}
              />
            )}
          </HStack>
        )}
        {onClose && (
          <Tooltip label="Close file" hasArrow placement="bottom">
            <IconButton
              aria-label="Close file"
              icon={<Icon as={X} boxSize={3.5} />}
              variant="ghost"
              size="xs"
              ml={1}
              onClick={onClose}
              color="text.muted"
              _hover={{ color: colorMode === 'dark' ? 'stone.100' : 'stone.800' }}
            />
          </Tooltip>
        )}
        {/* Share info icons - shown in read-only mode when viewing shares */}
        {readOnly && (expiresAt || burnAfterRead) && (
          <HStack spacing={2} ml={3} color="text.muted">
            {expiresAt && (
              <Tooltip label={`Expires: ${formatExpiry(expiresAt)}`} hasArrow placement="bottom">
                <HStack spacing={1}>
                  <Icon as={Clock} boxSize={3.5} />
                </HStack>
              </Tooltip>
            )}
            {burnAfterRead && (
              <Tooltip label="Burns after reading" hasArrow placement="bottom">
                <HStack spacing={1}>
                  <Icon as={Flame} boxSize={3.5} color="orange.500" />
                </HStack>
              </Tooltip>
            )}
          </HStack>
        )}
      </HStack>

      {/* Center: View mode toggle - only show in edit mode and when not hidden */}
      {!readOnly && !hideViewToggle && (
        <HStack
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          bg="bg.muted"
          borderRadius="md"
          p={1}
          spacing={1}
        >
          <Tooltip label="Split view" hasArrow placement="bottom">
            <IconButton
              {...getButtonProps('split')}
              aria-label="Split view"
              icon={<Columns2 size={14} />}
              borderRadius="md"
            />
          </Tooltip>
          <Tooltip label="Editor only" hasArrow placement="bottom">
            <IconButton
              {...getButtonProps('editor')}
              aria-label="Editor only"
              icon={<FileText size={14} />}
              borderRadius="md"
            />
          </Tooltip>
          <Tooltip label="Preview" hasArrow placement="bottom">
            <IconButton
              {...getButtonProps('preview')}
              aria-label="Preview"
              icon={<Monitor size={14} />}
              isDisabled={!hasContent}
              borderRadius="md"
            />
          </Tooltip>
        </HStack>
      )}

      <Spacer />

      {/* Right side: Actions */}
      <HStack spacing={2}>
        {/* Share info and actions for viewing published shares (non-editable) */}
        {hideViewToggle && !readOnly && (
          <>
            {/* Expiry info */}
            {expiresAt && (
              <Tooltip label={`Expires: ${formatExpiry(expiresAt)}`} hasArrow placement="bottom">
                <HStack
                  spacing={1}
                  px={2}
                  py={1}
                  bg={colorMode === 'dark' ? 'stone.700' : 'stone.100'}
                  borderRadius="md"
                  fontSize="xs"
                  color="text.muted"
                >
                  <Icon as={Clock} boxSize={3} />
                  <Text fontWeight="medium">
                    {isNeverExpires(expiresAt) ? 'Never expires' : new Date(expiresAt).toLocaleDateString()}
                  </Text>
                </HStack>
              </Tooltip>
            )}

            {/* Burn after read indicator */}
            {burnAfterRead && (
              <Tooltip label="Burns after reading" hasArrow placement="bottom">
                <HStack
                  spacing={1}
                  px={2}
                  py={1}
                  bg={colorMode === 'dark' ? 'orange.900' : 'orange.50'}
                  borderRadius="md"
                  fontSize="xs"
                  color="orange.500"
                >
                  <Icon as={Flame} boxSize={3} />
                  <Text fontWeight="medium">Burn</Text>
                </HStack>
              </Tooltip>
            )}

            {/* View Raw toggle */}
            {onToggleRawView && (
              <Tooltip label={showRawView ? 'View preview' : 'View raw markdown'} hasArrow placement="bottom">
                <Button
                  size="sm"
                  variant={showRawView ? 'solid' : 'outline'}
                  colorScheme={showRawView ? 'brand' : 'gray'}
                  leftIcon={<Icon as={showRawView ? Monitor : Code} boxSize={3.5} />}
                  onClick={onToggleRawView}
                >
                  {showRawView ? 'Preview' : 'Raw'}
                </Button>
              </Tooltip>
            )}

            {/* Share/Copy link button */}
            {shareUrl && (
              <Tooltip label={hasCopied('shareLink') ? 'Copied!' : 'Copy share link'} hasArrow placement="bottom">
                <Button
                  size="sm"
                  variant="solid"
                  colorScheme="brand"
                  leftIcon={<Icon as={hasCopied('shareLink') ? Check : Share2} boxSize={3.5} />}
                  onClick={handleCopyLink}
                >
                  Share
                </Button>
              </Tooltip>
            )}
          </>
        )}

        {/* Clone and Edit button - only in read-only mode */}
        {onEdit && readOnly && (
          <Tooltip label="Clone to editor" hasArrow placement="bottom">
            <IconButton
              aria-label="Clone to editor"
              icon={<Icon as={FilePlus2} boxSize={4} />}
              variant="ghost"
              size="sm"
              onClick={onEdit}
            />
          </Tooltip>
        )}

        {/* View Raw toggle - in read-only mode */}
        {readOnly && onToggleRawView && (
          <Tooltip label={showRawView ? 'View preview' : 'View raw markdown'} hasArrow placement="bottom">
            <IconButton
              aria-label={showRawView ? 'View preview' : 'View raw'}
              icon={<Icon as={showRawView ? Monitor : Code} boxSize={4} />}
              variant={showRawView ? 'solid' : 'ghost'}
              colorScheme={showRawView ? 'brand' : 'gray'}
              size="sm"
              onClick={onToggleRawView}
            />
          </Tooltip>
        )}

        {/* Theme toggle - only in read-only mode */}
        {readOnly && (
          <Tooltip label={colorMode === 'dark' ? 'Light mode' : 'Dark mode'} hasArrow placement="bottom">
            <IconButton
              aria-label="Toggle theme"
              icon={<Icon as={colorMode === 'dark' ? Sun : Moon} boxSize={4} />}
              variant="ghost"
              size="sm"
              onClick={toggleColorMode}
            />
          </Tooltip>
        )}

        {/* Preview in new tab - shown when viewing a published share in editor */}
        {!readOnly && shareUrl && (
          <Tooltip label="Preview as viewer" hasArrow placement="bottom">
            <IconButton
              aria-label="Preview as viewer"
              icon={<Icon as={ExternalLink} boxSize={4} />}
              variant="ghost"
              size="sm"
              onClick={() => window.open(shareUrl, '_blank')}
            />
          </Tooltip>
        )}

        {/* Edit button for modifiable shares in preview mode */}
        {!readOnly && isModifiable && viewMode === 'preview' && onEditModifiable && (
          <Tooltip label="Edit this share" hasArrow placement="bottom">
            <Button
              size="sm"
              colorScheme="brand"
              leftIcon={<Icon as={Pencil} boxSize={4} />}
              onClick={onEditModifiable}
            >
              Edit
            </Button>
          </Tooltip>
        )}

        {/* Update Share button - shown when editing a modifiable share with changes */}
        {!readOnly && isModifiable && viewMode !== 'preview' && isModified && onUpdateShare && (
          <Tooltip label="Save changes to share" hasArrow placement="bottom">
            <Button
              size="sm"
              colorScheme="brand"
              leftIcon={<Icon as={FileEdit} boxSize={4} />}
              onClick={onUpdateShare}
              isLoading={isUpdatingShare}
            >
              Update Share
            </Button>
          </Tooltip>
        )}
      </HStack>
    </Flex>
  )
}
